import React from 'react';
import { motion } from 'framer-motion';
import SidebarBrandBlock from '../oracle/SidebarBrandBlock';
import AurumButton from '../oracle/AurumButton';
import BoneButton from '../oracle/BoneButton';
import { COPY } from '../../contracts/copyMap';

// Reusable chrome for every onboarding step: brand lockup, progress dashes,
// "STEP n OF count" label, serif headline + italic subtitle, a content slot,
// and the BACK / CONTINUE footer. Steps supply only their body + validity.
//
//   ┌──────────────────────────────┐
//   │ [crest] MOGGED               │
//   │ ▰ ▭ ▭ ▭                       │  ← StepProgress
//   │ STEP 1 OF 4                   │
//   │ How old are you?              │  ← headline (serif)
//   │ We calibrate your benchmarks. │  ← subtitle (italic)
//   │ {children}                    │  ← step body
//   │ [BACK] [ CONTINUE → ]         │  ← footer
//   └──────────────────────────────┘

const StepProgress = ({ stepIndex, stepCount }) => (
  <div className="flex items-center gap-2" aria-hidden="true">
    {Array.from({ length: stepCount }).map((_, i) => (
      <span
        key={i}
        className={[
          'h-1 rounded-full transition-all duration-300',
          i === stepIndex ? 'w-8 bg-aurum' : 'w-4 bg-vein',
        ].join(' ')}
      />
    ))}
  </div>
);

const OnboardingShell = ({
  stepIndex,
  stepCount,
  headline,
  subtitle,
  children,
  onBack,
  onContinue,
  onExit,
  continueLabel = COPY.ONBOARDING_CONTINUE_CTA,
  continueDisabled = false,
  submitting = false,
  error,
}) => {
  const canBack = stepIndex > 0;

  return (
    <main className="flex min-h-screen items-center justify-center px-5 py-10">
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
        className="w-full max-w-xl rounded-[22px] border border-hairline bg-[rgba(13,13,12,0.72)] p-8 md:p-10 shadow-card-lift"
        aria-label="Onboarding"
      >
        <div className="mb-7">
          <SidebarBrandBlock variant="marketing" />
        </div>

        <StepProgress stepIndex={stepIndex} stepCount={stepCount} />

        <p className="mt-5 font-display uppercase tracking-[0.22em] text-[11px] text-bone-dim">
          {COPY.ONBOARDING_STEP_WORD} {stepIndex + 1} {COPY.ONBOARDING_OF_WORD}{' '}
          {stepCount}
        </p>

        <h1
          className="mt-3 font-display text-bone m-0"
          style={{ fontSize: '40px', lineHeight: 1.1 }}
        >
          {headline}
        </h1>
        {subtitle && (
          <p className="mt-3 font-display italic text-bone-dim m-0">{subtitle}</p>
        )}

        <div className="mt-8">{children}</div>

        {error && (
          <p
            role="alert"
            className="mt-5 font-display text-[13px] text-bone"
            style={{ color: '#E0A4A4' }}
          >
            {error}
          </p>
        )}

        <div className="mt-9 flex items-center gap-3">
          {canBack && (
            <BoneButton onClick={onBack} disabled={submitting} size="lg">
              {COPY.ONBOARDING_BACK_CTA}
            </BoneButton>
          )}
          <AurumButton
            variant="metallic"
            size="lg"
            fullWidth
            onClick={onContinue}
            disabled={continueDisabled || submitting}
            trailingIcon={<span aria-hidden="true">&rarr;</span>}
            className="flex-1"
            data-testid="onboarding-continue"
          >
            {continueLabel}
          </AurumButton>
        </div>

        {/* Quiet exit — onboarding is mandatory to proceed, but never a trap:
            signing out drops auth and AuthGate routes back to /sign-in. */}
        {onExit && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onExit}
              disabled={submitting}
              data-testid="onboarding-signout"
              className="font-display uppercase tracking-[0.22em] text-[11px] text-bone-dim transition-colors duration-200 hover:text-bone disabled:opacity-50 bg-transparent border-0 cursor-pointer"
            >
              {COPY.SIGN_OUT_CTA}
            </button>
          </div>
        )}
      </motion.section>
    </main>
  );
};

export default OnboardingShell;
